import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { FaPlay, FaCheckCircle } from 'react-icons/fa';
import { aboutContent } from '../config';

const AboutSection = () => {
  const fullText = aboutContent.title;
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < fullText.length) {
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + fullText[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, 50);

      return () => clearTimeout(timeout);
    }
  }, [currentIndex, fullText]);

  return (
    <section
      id="about"
      className="relative py-20 px-4 md:px-12 lg:px-24 bg-white overflow-hidden"
    >
      <div className="container mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Side - Images with Play Button */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative h-[500px] lg:h-[550px]"
          >
            {/* Image 01 - Left image with rounded corners */}
            <div className="absolute left-0 top-0 w-[55%] h-[65%] rounded-tl-[80px] rounded-br-[80px] overflow-hidden shadow-2xl z-10">
              <img
                src="/images/01 (2).webp"
                alt="Team collaboration"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Image 02 - Right overlapping image with rounded corners */}
            <div className="absolute right-0 bottom-0 w-[55%] h-[65%] rounded-tr-[80px] rounded-bl-[80px] overflow-hidden shadow-2xl z-20">
              <img
                src="/images/02 (3).webp"
                alt="Professional workspace"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Breathing Play Button positioned between images */}
            <motion.div
              animate={{
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute top-[35%] left-[45%] -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl cursor-pointer z-30 border-4 border-accent-blue/30"
            >
              <div className="w-16 h-16 bg-accent-blue rounded-full flex items-center justify-center">
                <FaPlay className="text-white text-xl ml-1" />
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            {/* Badge */}
            <div className="inline-block px-4 py-2 bg-accent-blue/10 rounded-full mb-4">
              <span className="text-accent-blue font-semibold text-sm tracking-wider">
                {aboutContent.badge}
              </span>
            </div>

            {/* Typing Animation Heading */}
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primary-navy leading-tight min-h-[140px]">
              {displayedText}
              <span className="animate-pulse">|</span>
            </h2>

            {/* Description */}
            <p className="text-gray-600 leading-relaxed">
              {aboutContent.description}
            </p>

            {/* Feature List */}
            <div className="space-y-3">
              {aboutContent.features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <FaCheckCircle className="text-accent-blue text-xl mt-1 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>{feature.title}:</strong> {feature.description}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA and Contact */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center pt-4">
              <motion.a
                href="#about"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-accent-blue text-white font-semibold rounded-lg shadow-lg hover:bg-accent-blue-dark transition-colors"
              >
                More About Us
              </motion.a>

              {/* Contact Person */}
              {/* <div className="flex items-center gap-3">
                <div className="relative w-12 h-12 rounded-full overflow-hidden border-2 border-accent-blue">
                  <Image
                    src="/images/person.webp"
                    alt="Contact person"
                    fill
                    className="object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Call to ask any question
                  </p>
                  <p className="font-bold text-primary-navy text-lg">
                    0123-456-7890
                  </p>
                </div>
              </div> */}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
